import React from 'react';
import { Edit3, ExternalLink, Loader2, Save, Trash2, X } from 'lucide-react';
import { getIntlLocale } from '../../../i18n/runtime';

export default function BookingRow({
  item,
  muted,
  cancelled,
  isEditing,
  editForm,
  rowBusy,
  onStartEdit,
  onEditFormChange,
  onSaveEdit,
  onCancelEdit,
  onCancelBooking,
}) {
  const start = new Date(item.start_at);
  const end = new Date(item.end_at);
  const isValidDate = !Number.isNaN(start.getTime());
  const isBusy = rowBusy === item.id;

  if (isEditing && editForm) {
    return (
      <div className="border-b last:border-b-0 border-[#E3E2E0] bg-[#FBFBFA] p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            value={editForm.title}
            onChange={(e) => onEditFormChange((prev) => ({ ...prev, title: e.target.value }))}
            className="px-3 py-2 bg-white border border-[#E3E2E0] rounded-lg text-sm"
          />
          <input
            value={editForm.attendees}
            onChange={(e) => onEditFormChange((prev) => ({ ...prev, attendees: e.target.value }))}
            placeholder="Attendees, comma separated"
            className="px-3 py-2 bg-white border border-[#E3E2E0] rounded-lg text-sm"
          />
          <input
            type="datetime-local"
            value={editForm.start_at}
            onChange={(e) => onEditFormChange((prev) => ({ ...prev, start_at: e.target.value }))}
            className="px-3 py-2 bg-white border border-[#E3E2E0] rounded-lg text-sm"
          />
          <input
            type="datetime-local"
            value={editForm.end_at}
            onChange={(e) => onEditFormChange((prev) => ({ ...prev, end_at: e.target.value }))}
            className="px-3 py-2 bg-white border border-[#E3E2E0] rounded-lg text-sm"
          />
          <textarea
            value={editForm.description}
            onChange={(e) => onEditFormChange((prev) => ({ ...prev, description: e.target.value }))}
            rows={2}
            className="md:col-span-2 px-3 py-2 bg-white border border-[#E3E2E0] rounded-lg text-sm resize-none"
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            onClick={() => onSaveEdit(item.id)}
            disabled={isBusy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A0A0A] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save changes
          </button>
          <button
            onClick={onCancelEdit}
            disabled={isBusy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#E3E2E0] px-3 py-1.5 text-sm text-[#191919] hover:bg-white disabled:opacity-50"
          >
            <X className="w-4 h-4" />
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-4 px-4 py-3 border-b last:border-b-0 border-[#E3E2E0] ${muted ? 'opacity-70' : ''}`}>
      <div className="w-14 text-center">
        <div className="text-[10px] uppercase text-[#6B6B6B]">
          {isValidDate ? start.toLocaleDateString(getIntlLocale(), { month: 'short' }) : '--'}
        </div>
        <div className="text-2xl font-semibold text-[#191919] leading-none">
          {isValidDate ? start.getDate() : '--'}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[#191919]">{item.title || item.lead_name || 'Booking'}</div>
        <div className="text-xs text-[#6B6B6B] truncate">
          {isValidDate ? `${start.toLocaleString(getIntlLocale())} - ${Number.isNaN(end.getTime()) ? '' : end.toLocaleTimeString(getIntlLocale())}` : 'No start time'}
        </div>
        <div className="text-xs text-[#6B6B6B] truncate">
          {item.status || 'scheduled'}{item.meet_link ? ' / Meet link ready' : ''}{item.calendar_error ? ` / ${item.calendar_error}` : ''}
        </div>
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        {item.meet_link && (
          <a href={item.meet_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg bg-[#E6F4EA] px-3 py-1.5 text-xs font-medium text-[#1E7F4E]" title="Open Meet link">
            <ExternalLink className="w-3.5 h-3.5" />
            Meet
          </a>
        )}
        {item.google_event_link && (
          <a href={item.google_event_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg bg-[#EAF2FB] px-3 py-1.5 text-xs font-medium text-[#0052CC]" title="Open Google event">
            <ExternalLink className="w-3.5 h-3.5" />
            Event
          </a>
        )}
        {!cancelled && (
          <>
            <button
              onClick={() => onStartEdit(item)}
              disabled={isBusy}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#E3E2E0] px-3 py-1.5 text-xs font-medium text-[#191919] hover:bg-[#F7F7F5] disabled:opacity-50"
            >
              <Edit3 className="w-3.5 h-3.5" />
              Reschedule
            </button>
            <button
              onClick={() => onCancelBooking(item.id)}
              disabled={isBusy}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#FFD0CC] bg-[#FFF3F2] px-3 py-1.5 text-xs font-medium text-[#B42318] disabled:opacity-50"
            >
              {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}
